import { HttpRequestConfig, HttpResponse } from '@/utils/http-utils';
import { Language, SiteConfiguration, defaultAppLanguage } from '@/entities/site.configuration';
import { StorageItems, StorageItemSiteInfo, PrivateLabelInfo } from '@/entities/storage.items';
import storageService from '@/services/storage.service';
import httpService from '@/services/http.service';
import Logger from '@/utils/logger.utils';
import cacheService from '@/services/cache.service';
import { CacheConfig } from '@/entities/cache';
import BrowserService from '@/services/browser.service';
import StringUtils from '@/utils/string-utils';
import ObjectUtils from '@/utils/object-utils';
import { ApiCallResponseStatus } from '@/entities/api.call.sresponse.tatus';
import { WidgetsInfo } from '@/entities/widget.info';
import { storeService } from '@/services/store.service';
import JSONUtils from '@/utils/json-utils';
import { PagesInfo } from '@/entities/page.info';
import { configService } from '@/services/config.service';
import { URLUtils } from '@ol/ol-ui-core';

class SiteConfigService {
  constructor() {
    Logger.debug('creating new instance of site.config.service');
  }

  private cacheName: string = 'siteConfiguration';

  public async initSiteInfo(): Promise<void> {
    // if no siteInfo in localStorage, set it.
    const hasSiteInfo: boolean = await storageService.hasSiteInfoInStorage();
    if (!hasSiteInfo) {
      await this.setSiteInfoInLocalStorage();
    } else {
      const plIdInUrl: string = StringUtils.getParamValue(window.location.href, 'plId');
      const plIdInLocalStorage: number = await storageService.getPrivateLabelId();
      if (!!plIdInUrl && `${plIdInUrl}` !== `${plIdInLocalStorage}`) {
        // if plId in url parameter is different from siteInfo localStorage, retrieve it by piId
        await this.setSiteInfoInLocalStorage();
      }
    }
  }

  /**
   * Important initialization point. Without proper initialization the application behaviour is erratic.
   * @see StorageService.hasSiteInfoInStorage
   */
  /* istanbul ignore next */
  public async setSiteInfoInLocalStorage(plId0: number = -1): Promise<void> {
    let apiEndpoint: string = await configService.getConfig('siteInfo');
    if (apiEndpoint) {
      let hostname = window.location.hostname;
      let plId = plId0 > 0 ? `${plId0}` : new URLSearchParams(window.location.search).get('plId');
      hostname = await URLUtils.encode(hostname);
      apiEndpoint = apiEndpoint.replace('{hostName}', hostname);
      if (!plId) {
        const storageItems: StorageItems = await storageService.getStorageItemsIfExists();
        plId = storageItems && storageItems.getSiteInfo() && storageItems.getSiteInfo().privateLabelInfo ? storageItems.getSiteInfo().privateLabelInfo.id.toString() : null;
      }
      if (plId) {
        plId = await URLUtils.encode(plId);
        apiEndpoint = apiEndpoint.replace('{plId}', plId);
      } else {
        apiEndpoint = StringUtils.removeParam(apiEndpoint, 'plId');
      }
      try {
        const cacheName: string = 'Siteinfo_Configs';
        const cacheKey: string = `siteinfo_hostname-${hostname}_plId-${plId}`;
        const cacheConfig: CacheConfig = {
          name: cacheName,
          key: cacheKey,
          options: {
            cacheKeepOneKeyOnly: true,
            timeToLive: -1,
            secondLevel: true,
          },
        };
        const config: HttpRequestConfig = {
          cache: cacheConfig,
        };
        const res: HttpResponse<any> = await httpService.get(apiEndpoint, config, true);
        if (res.data) {
          if (!res.data.privateLabelInfo || !res.data.siteConfiguration) {
            Logger.error('Missing siteConfiguration and/or privateLabelId values!');
            return;
          }
          localStorage.setItem('siteInfo', JSON.stringify(res.data));
        }
      } catch (e) {
        Logger.error('Missing siteInfo values');
        const apiCallResponseStatus: ApiCallResponseStatus = e.response.data;
        apiCallResponseStatus.original = e;
        throw apiCallResponseStatus;
      }
    } else {
      Logger.error('siteInfo url not configured in config.json');
    }
  }

  public async getSiteConfig(): Promise<SiteConfiguration> {
    const storageItemSiteInfo: StorageItemSiteInfo = await storageService.getSiteInfo();
    let siteConfigEndpoint: string = (storageItemSiteInfo || new StorageItemSiteInfo()).siteConfiguration;
    const plId: number = await storageService.getPrivateLabelId();
    if (plId && siteConfigEndpoint) {
      siteConfigEndpoint = siteConfigEndpoint.replace('{plId}', `${plId}`);
    }
    const cacheKey: string = `${plId}`;
    const cacheConfig: CacheConfig = {
      name: this.cacheName,
      key: cacheKey,
      options: {
        cacheKeepOneKeyOnly: false,
        timeToLive: -1,
        forceUpdate: false,
        secondLevel: true,
      },
    };
    const config: HttpRequestConfig = {
      cache: cacheConfig,
    };

    const siteConfigurationRes: HttpResponse<SiteConfiguration> = await (httpService.get<SiteConfiguration>(siteConfigEndpoint, config));
    const siteConfiguration: SiteConfiguration = siteConfigurationRes.data;
    if (process.env.NODE_ENV && process.env.NODE_ENV !== 'development'
      && process.env.NODE_ENV && process.env.NODE_ENV !== 'test'
      && process.env.NODE_ENV && process.env.NODE_ENV !== 'e2etest'
      && siteConfiguration && siteConfiguration.privateLabelId
      && siteConfiguration.privateLabelId !== plId
    ) {
      await cacheService.removeItem(this.cacheName, cacheKey);
    }
    if (
      (!siteConfigurationRes.hitCache && siteConfiguration && siteConfiguration.configurations) ||
      !storeService.getApiEndpoints() ||
      JSONUtils.getKeys(storeService.getApiEndpoints()).length === 0
    ) {
      await storeService.setWidgetsInfo(siteConfiguration.configurations.widgets);
      await storeService.setApiEndpoints(siteConfiguration.configurations.endpoints);
    }
    return siteConfiguration;
  }

  public async getApiEndpoints(): Promise<any> {
    const siteConfiguration: SiteConfiguration = await this.getSiteConfig();
    let apiEndpoints;
    if (siteConfiguration) {
      apiEndpoints = siteConfiguration.configurations.endpoints;
    }
    return apiEndpoints;
  }
  public async getWidgetsInfo(): Promise<WidgetsInfo> {
    const siteConfiguration: SiteConfiguration = await this.getSiteConfig();
    let widgetsInfo: WidgetsInfo = {};
    if (siteConfiguration) {
      widgetsInfo = siteConfiguration.configurations.widgets;
    }
    return widgetsInfo;
  }
  public async getPagesInfo(): Promise<PagesInfo> {
    const siteConfiguration: SiteConfiguration = await this.getSiteConfig();
    let pagesInfo: PagesInfo = {};
    if (siteConfiguration) {
      pagesInfo = siteConfiguration.configurations.pages;
    }
    return pagesInfo;
  }
  public async getApiEndpoint(keyName: string, variablesToReplace: { [key: string]: string[] } = {}): Promise<string> {
    const apiEndpoints: any = await this.getApiEndpoints();
    let apiEndpoint = ObjectUtils.getNestedValue(apiEndpoints, keyName);
    if (ObjectUtils.isUnDefined(apiEndpoint)) {
      return apiEndpoint;
    }
    if (ObjectUtils.isDefined(variablesToReplace)) {
      Object.keys(variablesToReplace).forEach((variableName: string) => {
        while (apiEndpoint.includes(`{${variableName}}`)) {
          /* istanbul ignore next */
          apiEndpoint = apiEndpoint.replace(`{${variableName}}`, variablesToReplace[variableName].join(`&${variableName}=`));
        }
      });
    }
    return apiEndpoint;
  }

  public async getSupportedBrowsertLanguage(): Promise<Language> {
    const siteConfiguration: SiteConfiguration = await this.getSiteConfig();
    const supportedLanguages: Array<Language> = siteConfiguration.languages;
    if (supportedLanguages && supportedLanguages.length === 1) {
      return supportedLanguages[0];
    }

    const languageCode: string = await BrowserService.getBrowserLanguage();
    let defaultLangeage: Language = null;
    if (ObjectUtils.isDefined(supportedLanguages)) {
      for (let i = 0; i < supportedLanguages.length; i++) {
        if (languageCode.startsWith(supportedLanguages[i].languageCode)) {
          return supportedLanguages[i];
        }
        if (supportedLanguages[i].defaultLanguage) {
          defaultLangeage = supportedLanguages[i];
        }
      }
    }
    return defaultLangeage || defaultAppLanguage;
  }
}

// Export a singleton instance in the global namespace
const siteConfigService: SiteConfigService = new SiteConfigService();
export default siteConfigService;
